import { memo } from 'react';
import type { ActionNode, ConfiguracionIA } from '../types';

interface NodePanelProps {
  node: ActionNode | null;
  configIA: ConfiguracionIA | null;
  onClose: () => void;
}

const NodePanel = memo(({ node, configIA: _configIA, onClose }: NodePanelProps) => {
  if (!node) return null;

  const { data } = node;
  const iaConfig = data.iaConfig;

  // Clean label
  const cleanLabel = data.label
    .replace(/^[🤖📮⚑📑📁▶✓○◉◫■]\s*/g, '')
    .replace(/\s*✨$/g, '');

  return (
    <div
      style={{
        position: 'absolute',
        right: 16,
        top: 16,
        bottom: 16,
        width: 280,
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 20,
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '14px 16px',
          borderBottom: '1px solid #f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Detalles
        </span>
        <button
          onClick={onClose}
          style={{
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: '#9ca3af',
            borderRadius: 6,
            transition: 'background 0.15s ease, color 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f3f4f6';
            e.currentTarget.style.color = '#4b5563';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#9ca3af';
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {/* Title */}
        <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', lineHeight: 1.4, marginBottom: 4 }}>
          {cleanLabel}
        </div>

        {/* Type */}
        <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
          {data.tipoAccion || data.tipo}
        </div>

        {/* Info */}
        <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <InfoRow label="Orden" value={String(data.orden)} />
          <InfoRow label="Código" value={data.codigoUnico} mono />
        </div>

        {/* Jump */}
        {data.saltaA && (
          <div style={{ marginTop: 20 }}>
            <div style={{
              fontSize: 10,
              fontWeight: 600,
              color: '#f59e0b',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              marginBottom: 6
            }}>
              Salta a
            </div>
            <div style={{
              fontSize: 12,
              color: '#1f2937',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
              background: '#fef3c7',
              padding: '6px 10px',
              borderRadius: 4,
              border: '1px solid #fde68a',
            }}>
              {Array.isArray(data.saltaA) ? data.saltaA.join(', ') : data.saltaA}
            </div>
          </div>
        )}

        {/* IA Config */}
        {data.tieneIA && iaConfig && (
          <div
            style={{
              marginTop: 20,
              padding: 14,
              background: '#ecfdf5',
              borderRadius: 6,
              border: '1px solid #d1fae5',
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 10
            }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
              <span style={{ fontSize: 10, fontWeight: 600, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                IA Configurada
              </span>
            </div>

            {iaConfig.tipoAnalisis && (
              <div style={{ fontSize: 12, color: '#065f46', marginBottom: 8 }}>
                <span style={{ color: '#6b7280', fontSize: 11 }}>Tipo: </span>
                {iaConfig.tipoAnalisis}
              </div>
            )}

            {iaConfig.criterios && iaConfig.criterios.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>Criterios:</div>
                {iaConfig.criterios.map((c, i) => (
                  <div key={i} style={{ fontSize: 11, color: '#065f46', paddingLeft: 10, marginTop: 4, position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0 }}>•</span>
                    {c}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      {data.accionId && (
        <div style={{ padding: 14, borderTop: '1px solid #f3f4f6' }}>
          <button
            onClick={() => {
              if ((window as any).scrollToAccion) {
                (window as any).scrollToAccion(data.codigoUnico);
              }
            }}
            style={{
              width: '100%',
              padding: '10px 14px',
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              color: '#374151',
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background 0.15s ease, border-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f9fafb';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}
          >
            Ver en editor
          </button>
        </div>
      )}
    </div>
  );
});

interface InfoRowProps {
  label: string;
  value: string;
  mono?: boolean;
}

function InfoRow({ label, value, mono }: InfoRowProps) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <span style={{ fontSize: 11, color: '#9ca3af' }}>{label}</span>
      <span
        style={{
          fontSize: 12,
          color: '#1f2937',
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace' : 'inherit',
          textAlign: 'right',
          maxWidth: '60%',
          wordBreak: 'break-all',
        }}
      >
        {value}
      </span>
    </div>
  );
}

NodePanel.displayName = 'NodePanel';

export default NodePanel;
